import axios from 'axios';
import { useCallback, useEffect, useState, Dispatch, SetStateAction } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RoutePath } from 'src/router';

import { ServerErrorAdapter } from '@itm/shared-frontend/lib/utils';
import { LoginResponse } from '@itm/shared-frontend/lib/components/initial';

import { exchangeSsoCode } from 'src/api/auth/auth';

import { ExchangeRequest, ServerError, ServerFormErrors } from 'src/types';

type Props = Readonly<{
  setLoginResponseData: Dispatch<SetStateAction<LoginResponse | undefined>>;
}>;

/**
 * SSO Callback Component
 *
 * Handles the callback from the SSO provider (Azure AD, etc.)
 *
 * URL format: /auth/sso/callback?code=...&error=...&error_description=...
 */
function SsoCallback({ setLoginResponseData }: Props) {
  const [searchParams] = useSearchParams();
  const [serverErrorMessages, setServerErrorMessages] = useState<ServerFormErrors>([]);

  const code = searchParams.get('code');
  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  const requestLoginResponse = useCallback(async () => {
    if (!code) return;
    setServerErrorMessages((v) => (v.length ? [] : v));
    try {
      const payload: ExchangeRequest = { code };
      const res = await exchangeSsoCode(payload);
      setLoginResponseData(res.data);
    } catch (e) {
      if (axios.isCancel(e)) return;
      const serverErrors = new ServerErrorAdapter(e as ServerError);
      setServerErrorMessages(serverErrors.combine());
    }
  }, [code, setLoginResponseData]);

  useEffect(() => {
    requestLoginResponse();
  }, [requestLoginResponse]);

  // TODO: Update when Figma is ready, remove all inline styles
  // Show error state
  if (error || !!serverErrorMessages.length) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <h1 style={{ color: '#d32f2f', marginBottom: '16px' }}>Authentication Failed</h1>
        <p style={{ color: '#666', marginBottom: '24px', maxWidth: '500px' }}>{error || serverErrorMessages[0]}</p>
        {errorDescription && (
          <p style={{ color: '#666', marginBottom: '24px', maxWidth: '500px' }}>Description: {errorDescription}</p>
        )}

        <Link
          to={RoutePath.login}
          style={{
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
          }}
        >
          Return to Login
        </Link>
      </div>
    );
  }

  // Show loading state
  return (
    <div style={{ textAlign: 'center', marginTop: '20px' }}>
      <h2>Completing SSO Authentication...</h2>
      <p style={{ color: '#666', marginTop: '10px' }}>Please wait while we securely log you in.</p>
    </div>
  );
}

export default SsoCallback;
